import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

// Webinar CRUD — backend at /webinar (see docs/webinar-api.md in the portal backend).
// Notes that shaped this slice:
//  - POST and PUT ALWAYS take an array under `data`, even for a single record.
//  - PUT carries `webinar_id` (a JSON number, not a string) in the BODY.
//  - PUT, PATCH and DELETE return no record — callers must refetch after mutating.
//  - Standard portal envelope { status, message, data, meta }; `meta` only on list.
//  - ONLY ONE webinar can be active. The server enforces it: activating one
//    deactivates the previous one in the same transaction, so the UI never has to
//    send a second call to turn the old one off — just refetch after activating.
//  - `poster` is the bare file name from the upload endpoint. Reads also carry
//    `poster_url` (absolute) for display — send back `poster`, render `poster_url`.

const initialState = {
  // list endpoint (paginated)
  table: {
    loading: false,
    data: [],
    meta: {},
  },
  // single webinar (get one)
  single: {
    loading: false,
    data: null,
  },
  // shared flag for create / update / status / delete
  mutating: false,
  // poster upload runs on its own flag so the form can disable just that button
  uploading: false,
};

// 1. LIST ------------------------------------------------------------------
export const getWebinars = createAsyncThunk('webinar/getWebinars', async (params = {}) => {
  const { page = 1, limit = 10, search = '', sortBy, sortDirection } = params;

  const searchParams = new URLSearchParams({
    page,
    limit,
    search: search ?? '',
  });
  if (sortBy) {
    searchParams.append('sortBy', sortBy);
    searchParams.append('sortDirection', sortDirection || 'asc');
  }

  const res = await apiRequest({
    method: 'get',
    url: `webinar?${searchParams.toString()}`,
  });
  return res;
});

// 2. GET ONE ---------------------------------------------------------------
export const getWebinar = createAsyncThunk('webinar/getWebinar', async (webinarId) => {
  const res = await apiRequest({
    method: 'get',
    url: `webinar/${webinarId}`,
  });
  return res;
});

// 3. CREATE (array payload, all-or-nothing) --------------------------------
export const createWebinars = createAsyncThunk('webinar/createWebinars', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: 'webinar',
    data: { data: Array.isArray(data) ? data : [data] },
  });
  return res;
});

// 4. UPDATE (array payload, webinar_id in body as a number) ----------------
export const updateWebinars = createAsyncThunk('webinar/updateWebinars', async (data) => {
  const list = (Array.isArray(data) ? data : [data]).map((item) => ({
    ...item,
    webinar_id: Number(item.webinar_id), // API rejects a string id
  }));
  const res = await apiRequest({
    method: 'put',
    url: 'webinar',
    data: { data: list },
  });
  return res;
});

// 5. TOGGLE ACTIVE / INACTIVE ---------------------------------------------
// PATCH /webinar/:id/status — what the grid switch calls, so the whole row does
// not have to be sent back to flip one flag. Activating deactivates whichever
// webinar was active, server-side, so ALWAYS refetch the list after this.
export const updateWebinarStatus = createAsyncThunk(
  'webinar/updateWebinarStatus',
  async ({ webinarId, isActive }) => {
    const res = await apiRequest({
      method: 'patch',
      url: `webinar/${webinarId}/status`,
      data: { is_active: Boolean(isActive) },
    });
    return res;
  },
);

// 6. DELETE (soft delete, id in URL) ---------------------------------------
export const deleteWebinar = createAsyncThunk('webinar/deleteWebinar', async (webinarId) => {
  const res = await apiRequest({
    method: 'delete',
    url: `webinar/${webinarId}`,
  });
  return res;
});

// 7. UPLOAD POSTER ---------------------------------------------------------
// POST /webinar/upload-poster — multipart/form-data, field name `poster`
// (images, max 4 MB). Returns the bare filename in data.filename; pass that as
// `poster` on the create / update call. Uploading alone attaches nothing.
export const uploadWebinarPoster = createAsyncThunk(
  'webinar/uploadWebinarPoster',
  async (file, action) => {
    const formData = new FormData();
    formData.append('poster', file);
    const res = await apiRequest({
      method: 'post',
      url: 'webinar/upload-poster',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (res?.status === 'success') return res;
    return action.rejectWithValue(res);
  },
);

const webinarSlice = createSlice({
  name: 'webinar',
  initialState,
  reducers: {
    clearWebinar: (state) => {
      state.single = { loading: false, data: null };
    },
  },
  extraReducers: (builder) => {
    // ---------------- LIST ----------------
    builder.addCase(getWebinars.pending, (state) => {
      state.table.loading = true;
    });
    builder.addCase(getWebinars.fulfilled, (state, action) => {
      state.table.loading = false;
      state.table.data = action.payload?.data || [];
      state.table.meta = action.payload?.meta || {};
    });
    builder.addCase(getWebinars.rejected, (state) => {
      state.table.loading = false;
    });

    // ---------------- GET ONE ----------------
    builder.addCase(getWebinar.pending, (state) => {
      state.single.loading = true;
    });
    builder.addCase(getWebinar.fulfilled, (state, action) => {
      state.single.loading = false;
      state.single.data = action.payload?.data || null;
    });
    builder.addCase(getWebinar.rejected, (state) => {
      state.single.loading = false;
    });

    // ---------------- CREATE ----------------
    builder.addCase(createWebinars.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(createWebinars.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(createWebinars.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- UPDATE ----------------
    builder.addCase(updateWebinars.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(updateWebinars.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(updateWebinars.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- STATUS TOGGLE ----------------
    builder.addCase(updateWebinarStatus.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(updateWebinarStatus.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(updateWebinarStatus.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- DELETE ----------------
    builder.addCase(deleteWebinar.pending, (state) => {
      state.mutating = true;
    });
    builder.addCase(deleteWebinar.fulfilled, (state) => {
      state.mutating = false;
    });
    builder.addCase(deleteWebinar.rejected, (state) => {
      state.mutating = false;
    });

    // ---------------- POSTER UPLOAD ----------------
    builder.addCase(uploadWebinarPoster.pending, (state) => {
      state.uploading = true;
    });
    builder.addCase(uploadWebinarPoster.fulfilled, (state) => {
      state.uploading = false;
    });
    builder.addCase(uploadWebinarPoster.rejected, (state) => {
      state.uploading = false;
    });
  },
});

export const { clearWebinar } = webinarSlice.actions;
export default webinarSlice.reducer;
